<template>
  <div class="offsite-page">

    <!-- ── Hero / Save the date ───────────────────────────────────────────── -->
    <section class="os-hero">
      <div class="os-hero__stage">
        <div class="os-hero__col">
          <!-- Palm-shadow panel with the "LA" knocked out of a black overlay -->
          <div class="os-hero__panel">
            <svg class="os-hero__la" viewBox="0 0 100 56" aria-hidden="true">
              <defs>
                <!-- The mask region runs past the viewBox so the extruded glyph
                     copies below are not clipped out of the mask. -->
                <mask :id="laMaskId" maskUnits="userSpaceOnUse" x="0" y="0" width="100" height="62">
                  <rect x="0" y="0" width="100" height="56" fill="#fff" />
                  <!-- L and A pushed to opposite ends of the bar; baseline sits
                       where the solid bar begins. Each glyph is drawn twice — the
                       second copy 3 units lower — so the knockout stays fully
                       opaque across the bottom pixel row. Without it that row is
                       part rect, part glyph, and the leftover alpha reads as a
                       hairline where the letters meet the bar. -->
                  <g fill="#000" font-family="'FH Oscar', Helvetica, Arial, sans-serif" font-weight="700" font-size="78">
                    <text x="0" y="56" text-anchor="start">L</text>
                    <text x="0" y="59" text-anchor="start">L</text>
                    <text x="100" y="56" text-anchor="end">A</text>
                    <text x="100" y="59" text-anchor="end">A</text>
                  </g>
                </mask>
              </defs>
              <!-- crispEdges snaps the bottom edge to a whole device pixel instead
                   of antialiasing it over the panel image below. -->
              <rect x="0" y="0" width="100" height="56" fill="#000"
                    shape-rendering="crispEdges" :mask="`url(#${laMaskId})`" />
            </svg>

            <div class="os-hero__lockup">
              <p class="os-hero__brand">ORE<br />OFFSITE:<br />STUDIO</p>
              <p class="os-hero__at">At ASLA</p>
            </div>
          </div>

          <!-- Date block -->
          <div class="os-hero__date">
            <span class="os-hero__month">SEPT.</span>
            <span class="os-hero__days">16&ndash;17</span>
            <span class="os-hero__time">8 AM &ndash; 5 PM</span>
          </div>

          <!-- Lower panel -->
          <div class="os-hero__panel os-hero__panel--low" aria-hidden="true"></div>
        </div>

        <ul class="os-hero__rail" aria-hidden="true">
          <li v-for="word in railWords" :key="word">{{ word }}</li>
        </ul>
      </div>
    </section>

    <!-- ── Where / When + map ─────────────────────────────────────────────── -->
    <section class="os-where">
      <div class="os-where__map">
        <img src="@/assets/img/offsite/map-moxy-la.jpg"
             alt="Map showing the Moxy Downtown Los Angeles near the Los Angeles Convention Center" loading="lazy" />
      </div>
      <div class="os-where__detail">
        <dl>
          <dt>Where:</dt>
          <dd>
            <strong>The Moxy + AC Hotel</strong><br />
            Moxy Studio &ndash; Mezzanine Level<br />
            Downtown Los Angeles<br />
            1260 South Figueroa Street
          </dd>
          <dt>When:</dt>
          <dd>
            September 16 &amp; 17, 2026<br />
            8 AM &ndash; 5 PM
          </dd>
        </dl>
      </div>
    </section>

    <!-- ── OFFSITE: STUDIO details ────────────────────────────────────────── -->
    <section class="os-studio">
      <div class="os-shell">
        <h2 class="os-section-title os-section-title--light">ORE OFFSITE: STUDIO</h2>

        <ul class="os-studio__statement">
          <li v-for="phrase in studioStatement" :key="phrase">
            {{ phrase }}<span class="os-studio__stop" aria-hidden="true">.</span>
          </li>
        </ul>

        <p class="os-studio__body">
          Join us at ORE OFFSITE: STUDIO to see what&rsquo;s new, bring a project to review,
          explore new finishes and materials, or talk through a custom opportunity. Our Design
          Development team and Sales Engineers will be on-site all day&mdash;whether you need to
          dive into project details, grab coffee and a pastry in the morning, or sit down for
          lunch in the afternoon.
        </p>
      </div>

      <div class="os-photos">
        <img src="@/assets/img/offsite/studio-1.jpg"
             alt="Attendees examining ORE fabricated elements at an ORE event" loading="lazy" />
        <img src="@/assets/img/offsite/studio-2.jpg"
             alt="An ORE team member walking guests through planters and fire features" loading="lazy" />
      </div>
    </section>

    <!-- ── The legacy of OFFSITE ──────────────────────────────────────────── -->
    <section class="os-legacy">
      <div class="os-shell">
        <p class="os-eyebrow">The Legacy of</p>
        <h2 class="os-legacy__title">ORE OFFSITE</h2>
        <p>
          The inspiration for ORE OFFSITE was to create a space for real, personal connection. We
          wanted a place where designers could slow down, talk, discover something new, and enjoy
          being together.
        </p>
        <p>
          We&rsquo;ve gathered designers, artists, and friends in remarkable spaces, from the
          <a href="https://www.moca.org/" target="_blank" rel="noopener">Museum of Contemporary
          Art</a> in Los Angeles to the <a href="https://www.barnesfoundation.org/" target="_blank"
          rel="noopener">Barnes Foundation</a> in Philadelphia. We&rsquo;ve shared evenings centered
          around inspiration, featuring visionary speakers like
          <a href="https://www.cliffgartenstudio.com/" target="_blank" rel="noopener">Cliff
          Garten</a>, <a href="https://www.echelman.com/" target="_blank" rel="noopener">Janet
          Echelman</a>, and <a href="https://leylaacaroglu.com/" target="_blank" rel="noopener">Leyla
          Acaroglu</a>.
        </p>
        <p>
          OFFSITE became much more than an event; it became a cherished tradition rooted entirely in
          relationship-building. That same focus is exactly what brings us to ORE OFFSITE: STUDIO
          today.
        </p>
      </div>
    </section>

    <!-- ── Previous OFFSITES ──────────────────────────────────────────────── -->
    <section class="os-previous">
      <div class="os-shell os-shell--wide">
        <h2 class="os-section-title os-section-title--light">Previous OFFSITES</h2>
        <div class="os-previous__grid">
          <router-link
            v-for="event in previousOffsites"
            :key="event.slug"
            :to="`/${event.slug}`"
            class="os-previous__card"
          >
            <img :src="event.cardImage" :alt="`ORE OFFSITE ${event.year} — ${event.city}`" loading="lazy" />
            <span class="os-previous__city">{{ event.city }}</span>
            <span class="os-previous__year">{{ event.year }}</span>
          </router-link>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount } from 'vue'
import { offsiteYears } from '@/data/offsiteYears.js'

// Unique id so the SVG mask never collides with another instance on the page.
const laMaskId = 'offsite-la-mask'

const railWords = ['Relax', 'Chat', 'Eat', 'Drink']

const studioStatement = ['Connect', 'Explore', 'Take a break']

// Newest first, so 2025 leads the grid.
const previousOffsites = [...offsiteYears].reverse()

onMounted(() => document.body.classList.add('page-offsite'))
onBeforeUnmount(() => document.body.classList.remove('page-offsite'))
</script>

<style lang="scss" scoped>
$black: #000;
$white: #fff;
$orange: #ff4c00;
$green-dark: #001610;
$mist: #ebf8ef;
$font: 'FH Oscar', Helvetica, Arial, sans-serif;

.offsite-page {
  background: $black;
  color: $white;
  font-family: $font;
}

.os-shell {
  max-width: 760px;
  margin: 0 auto;
  padding: 0 24px;

  &--wide { max-width: 1100px; }
}

.os-eyebrow {
  font-weight: 500;
  font-size: 0.8125rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: $orange;
  margin-bottom: 14px;
}

.os-section-title {
  font-weight: 600;
  font-size: clamp(1.75rem, 4vw, 2.75rem);
  line-height: 1.15;
  margin-bottom: 24px;
  color: $white;

  &--light { font-weight: 300; }
}

// ── Hero ─────────────────────────────────────────────────────────────────────
// Every dimension below is a ratio taken off the InDesign save-the-date, expressed
// against --p (the light panel's width). --s is the stage width. Measured values:
// panel = 49.4% of stage, panel1 h = 1.187p, date type spans the full panel width.
.os-hero {
  --s: min(1100px, 100vw);
  --p: calc(var(--s) * 0.494);
  background: $black;
  // The site header is position:fixed, and below ~1100px the rail shares its lane
  // with the quote button and menu toggle — horizontal separation only exists on
  // wide viewports, so clear it vertically instead. The header is 81px tall and
  // grows to 119px at its own 1025px breakpoint; these floors keep ~23px under it.
  padding: max(calc(var(--s) * 0.06), 104px) 0 calc(var(--s) * 0.05);

  @media (min-width: 1025px) {
    padding-top: max(calc(var(--s) * 0.06), 144px);
  }
}

.os-hero__stage {
  position: relative;
  max-width: 1100px;
  margin: 0 auto;
  padding-left: calc(var(--s) * 0.079);
  padding-right: calc(var(--s) * 0.091);
}

.os-hero__col { width: var(--p); }

.os-hero__panel {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1.18;    // 0.56 LA band + 0.62 solid bar
  background-image: url('@/assets/img/offsite/palm-shadow.jpg');
  background-size: cover;
  background-position: 18% 40%;

  &--low {
    aspect-ratio: 1 / 0.414;
    margin-top: calc(var(--p) * 0.028);
    background-position: 40% 70%;
  }
}

// Black plate with the LA knocked out of it, so the panel shows through the letters.
.os-hero__la {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(var(--p) * 0.56);    // full cap height of the LA
  display: block;
}

.os-hero__lockup {
  position: absolute;
  left: calc(var(--p) * 0.104);
  top: calc(var(--p) * 0.634);      // 0.56 LA band + 0.12 of the solid bar
  color: $black;
}

.os-hero__brand {
  color: $black;
  font-weight: 700;
  font-size: calc(var(--p) * 0.085);
  line-height: 0.98;
  letter-spacing: -0.01em;
}

.os-hero__at {
  color: $black;
  font-weight: 300;
  font-size: calc(var(--p) * 0.060);
  line-height: 1.2;
  letter-spacing: 0.01em;
  margin-top: calc(var(--p) * 0.012);
}

.os-hero__date {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: calc(var(--p) * 0.058);
  font-weight: 500;
  letter-spacing: -0.01em;

  // Brushed-silver treatment from the mock.
  background: linear-gradient(
    170deg,
    #ffffff 0%,
    #d6d6d6 32%,
    #8a8a8a 52%,
    #efefef 72%,
    #b4b4b4 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.os-hero__month { font-size: calc(var(--p) * 0.410); line-height: 0.79; }
.os-hero__days  { font-size: calc(var(--p) * 0.410); line-height: 0.75; }
.os-hero__time  { font-size: calc(var(--p) * 0.186); line-height: 1; }

.os-hero__rail {
  position: absolute;
  top: 0;
  right: calc(var(--s) * 0.021);
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;   // grouped near the top so all four sit above the fold
  align-items: center;
  gap: calc(var(--s) * 0.028);
  font-weight: 700;
  font-size: calc(var(--s) * 0.036);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $white;

  li { writing-mode: vertical-rl; }
}

// Below the rail breakpoint the column goes full width and the rail is dropped.
@media (max-width: 700px) {
  .os-hero {
    --s: 100vw;
    --p: calc(var(--s) - 48px);
  }
  .os-hero__stage { padding-left: 24px; padding-right: 24px; }
  .os-hero__rail { display: none; }
}

// ── Where / When ─────────────────────────────────────────────────────────────
.os-where {
  display: grid;
  grid-template-columns: 58.3% 41.7%;   // map / detail split from the email layout
  background: $black;
  max-width: 1100px;                    // scales with the rest of the page, not the window
  margin: 0 auto;

  @media (max-width: 700px) { grid-template-columns: 1fr; }
}

.os-where__map {
  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    aspect-ratio: 1.427;
  }
}

.os-where__detail {
  display: flex;
  align-items: center;
  justify-content: center;   // was hard-left, leaving the pair looking offset
  padding: clamp(24px, 4vw, 48px);

  dt {
    font-weight: 700;
    font-size: clamp(1rem, 1.6vw, 1.25rem);
    color: $white;
    margin-bottom: 4px;
  }

  dd {
    margin: 0 0 clamp(16px, 2.4vw, 28px);
    font-weight: 300;
    font-size: clamp(0.9375rem, 1.5vw, 1.1875rem);
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.92);

    &:last-child { margin-bottom: 0; }
  }
}

// ── Event photography ────────────────────────────────────────────────────────
.os-photos {
  display: grid;
  grid-template-columns: 1fr 1fr;
  max-width: 1100px;
  margin: clamp(28px, 4vw, 48px) auto 0;

  img {
    display: block;
    width: 100%;
    aspect-ratio: 1.5;   // native 3:2 of the supplied photography
    object-fit: cover;
  }

  @media (max-width: 700px) { grid-template-columns: 1fr; }
}

// ── STUDIO details ───────────────────────────────────────────────────────────
.os-studio {
  background: $mist;
  color: $green-dark;
  padding: 88px 0;

  .os-shell { display: flex; flex-direction: column; align-items: flex-start; }
  .os-section-title { color: $green-dark; }
}

// Three-phrase statement set as a ruled band so it reads as a deliberate
// element in the section rather than loose text. Orange full stops carry the
// ORE accent through; the hairlines echo the capabilities band elsewhere.
.os-studio__statement {
  --gap: clamp(28px, 6vw, 76px);
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--gap);
  width: 100%;
  margin: clamp(18px, 2.4vw, 26px) 0 clamp(28px, 3.6vw, 40px);
  padding: clamp(16px, 2.2vw, 24px) 0;
  border-top: 1px solid rgba(0, 22, 16, 0.2);
  border-bottom: 1px solid rgba(0, 22, 16, 0.2);

  li {
    position: relative;
    font-weight: 500;
    font-size: clamp(1.25rem, 3.2vw, 2rem);
    line-height: 1.15;
    letter-spacing: -0.015em;
    color: $green-dark;
    white-space: nowrap;

    // hairline sitting in the middle of the gap, between phrases only
    & + li::before {
      content: '';
      position: absolute;
      left: calc(var(--gap) * -0.5);
      top: 0.08em;
      bottom: 0.08em;
      width: 1px;
      background: rgba(0, 22, 16, 0.22);
    }
  }

  @media (max-width: 560px) {
    --gap: 12px;
    flex-direction: column;
    li + li::before { display: none; }
  }
}

.os-studio__stop {
  color: $orange;
}

.os-studio__body {
  color: $green-dark;
  font-size: 1.0625rem;
  line-height: 1.75;
  margin-bottom: 28px;
}


// ── Legacy ───────────────────────────────────────────────────────────────────
.os-legacy {
  padding: 88px 0;

  .os-eyebrow { margin-bottom: 2px; }

  // :not() keeps the orange .os-eyebrow from being caught by this body-copy rule.
  p:not(.os-eyebrow) {
    font-size: 1.0625rem;
    line-height: 1.75;
    color: rgba(255, 255, 255, 0.85);

    & + p { margin-top: 20px; }
  }
}

// Inline links: ORE orange on the dark section, with an underline offset far
// enough to clear the descenders. The colour is the accent already used for the
// eyebrow, so the links read as part of the palette rather than default link blue.
.os-legacy a {
  color: $orange;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 0.18em;
  transition: color 0.2s, text-decoration-thickness 0.2s;

  &:hover,
  &:focus-visible {
    color: $white;
    text-decoration-thickness: 2px;
  }

  &:focus-visible {
    outline: 2px solid $orange;
    outline-offset: 3px;
  }
}

.os-legacy__title {
  color: $white;
  font-weight: 700;
  font-size: clamp(2.5rem, 8vw, 5rem);
  line-height: 1;
  margin-bottom: 28px;
}

// ── Previous OFFSITES ────────────────────────────────────────────────────────
.os-previous {
  padding: 0 0 88px;

  .os-section-title { text-align: center; }
}

.os-previous__grid {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: 18px;

  @media (max-width: 900px) {
    grid-auto-flow: row;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 520px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

.os-previous__card {
  display: block;
  text-decoration: none;
  color: $white;
  transition: transform 0.3s;

  img {
    display: block;
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border-radius: 10px;
    filter: grayscale(20%);
    transition: filter 0.3s;
    margin-bottom: 10px;
  }

  .os-previous__city {
    display: block;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.3;
  }

  .os-previous__year {
    display: block;
    font-weight: 300;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.65);
  }

  &:hover {
    transform: translateY(-4px);
    img { filter: grayscale(0%); }
  }
}

// ── Sign off ─────────────────────────────────────────────────────────────────
</style>

<style lang="scss">
.page-offsite .main-footer {
  display: none;
}

// Nav sits over the black hero until the header picks up its scrolled state.
// Until then it is fully transparent, so the vertical rail would scroll visibly
// underneath it. A black scrim reads as nothing against the black hero and keeps
// the nav legible; it stops applying the moment the frosted state takes over.
.page-offsite .main-header:not(.is-scrolled) {
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.92) 40%, rgba(0, 0, 0, 0) 100%);
}

.page-offsite .main-header:not(.is-scrolled) {
  .main-header__nav-list a {
    color: #fff !important;
  }
  .main-header__logo svg path,
  .main-header__logo svg rect,
  .main-header__logo svg polygon {
    fill: #fff !important;
  }
}
</style>
